// Reads the Tek Labs volume discount tiers the app exposes on product pages, so everything that shows
// tier prices on the storefront works them out the same way. Tek applies the discount itself in the
// cart; this only predicts what it will charge. Used by the DTC volume pricing widget and the refill
// packs.
if (!window.TekVolumePricing) {
  const parseIds = (value) =>
    String(value || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);

  const getState = () => window.tlDiscounts || window.tlDiscount || null;

  // Tek only exposes the groups that apply to the current product, populated once its rules load.
  const getGroups = () => {
    const groups = getState()?.discountsGroup;
    if (!Array.isArray(groups)) return [];

    return groups.filter(
      (group) => group?.status !== false && Array.isArray(group?.discountTiers) && group.discountTiers.length
    );
  };

  // Matches a variant id against product_ids as well, as the DTC widget always has.
  const findVariantGroup = (variantId) => {
    const id = String(variantId || '');
    if (!id) return null;

    return (
      getGroups().find(
        (group) => parseIds(group.variant_ids).includes(id) || parseIds(group.product_ids).includes(id)
      ) || null
    );
  };

  const findProductGroup = (productId) => {
    const id = String(productId || '');
    if (!id) return null;

    return getGroups().find((group) => parseIds(group.product_ids).includes(id)) || null;
  };

  // A rule counts its minimum per variant, or, set to "product", across all of a product's variants in
  // the cart together, with that combined quantity setting the tier for each of them.
  const countsPerProduct = (group) => group?.minimum_quantity_apply_on === 'product';

  // The tier with the highest minimum the quantity reaches, or null below the first tier.
  const getMatchedTier = (group, quantity) => {
    if (!group) return null;

    return (
      group.discountTiers
        .map((tier) => ({ minimum: Number.parseInt(tier?.minimum, 10) || 1, tier }))
        .sort((left, right) => left.minimum - right.minimum)
        .reduce((matched, entry) => (quantity >= entry.minimum ? entry : matched), null)?.tier || null
    );
  };

  // The tier's `off` as a number: a percentage, or a fixed amount for non-percentage groups.
  const getOff = (tier) => {
    const off = Number.parseFloat(tier?.off || 0);
    return Number.isNaN(off) ? null : off;
  };

  // A fixed amount comes through either in cents or in currency units; anything over 100 is cents.
  const getAmountOff = (tier) => {
    const off = getOff(tier);
    if (off === null) return null;

    return off > 100 ? Math.round(off) : Math.round(off * 100);
  };

  const getTierUnitPrice = (group, tier, basePrice) => {
    if (basePrice === null || basePrice === undefined) return null;

    const off = getOff(tier);
    if (off === null) return basePrice;

    if (group?.value_type === 'percentage') {
      return Math.max(Math.round((basePrice * (100 - off)) / 100), 0);
    }

    return Math.max(basePrice - getAmountOff(tier), 0);
  };

  const getUnitPrice = (group, basePrice, quantity) => {
    const tier = getMatchedTier(group, quantity);
    return tier ? getTierUnitPrice(group, tier, basePrice) : basePrice;
  };

  // Tek fills its groups only after fetching the product and then its rules (from a day-long cache,
  // or the network once the rules change), often after the page has loaded, and fires no event when
  // it does. So this polls until the groups appear and then calls back once. A product with no rule
  // never gets groups, and the polling gives up after the timeout.
  const READY_POLL_INTERVAL = 200;
  const READY_TIMEOUT = 15000;

  const whenReady = (callback) => {
    const startedAt = Date.now();

    const check = () => {
      if (getGroups().length) {
        callback();
        return;
      }

      if (Date.now() - startedAt < READY_TIMEOUT) setTimeout(check, READY_POLL_INTERVAL);
    };

    check();
  };

  window.TekVolumePricing = {
    parseIds,
    getState,
    getGroups,
    findVariantGroup,
    findProductGroup,
    countsPerProduct,
    getMatchedTier,
    getOff,
    getAmountOff,
    getTierUnitPrice,
    getUnitPrice,
    whenReady,
  };
}
